import React, { useEffect, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { WizardInpuHorizontal } from 'components/wizard';
import LoadingOverlay from 'components/common/LoadingOverlay';
import useGetCountry from 'hooks/customHooks/useGetCountry';
import { useAuth } from 'hooks/useAuth';
import { getListOptions, getValueObj, isValidEmail as isValidEmailInput } from 'helpers/utils';
import { genFleetFormSchema } from '../../config';
import FooterActionBtn from 'components/pages/FooterActionBtn';
import fleetServices from 'services/fleet.services';
import { commonConfig, formLayout } from 'helpers/formLayoutConfig';
import useFetchGroup from 'pages/Group/services/useFetchGroup';
import {
  PAYMENT_POINT_FLEET,
  PAYMENT_POINT,
  ROLES,
  YupMessage,
  DUPLICATE_EMAIL,
  FLEET
} from 'constants/index';
import useFetchCompany from 'pages/Company/services/useFetchCompany';
import StripeConnectStatus from 'components/pages/StripeConnectStatus';
import PrincipalDetailForm from 'components/pages/PrincipalDetailForm';
import companyServices from 'services/company.services';
import useCheckDuplicateEmail from 'hooks/customHooks/useCheckDuplicateEmail';
import useCheckValidABN from 'hooks/customHooks/useCheckValidABN';
import { FLEET_PRINCIPAL } from 'components/pages/PrincipalDetailForm/useAuthorizePrincipal';
import { useFleetContext } from 'pages/Fleet/FleetContext';
import LoadingButton from 'components/common/LoadingButton';
import useGetFleetInstantPayoutType from 'hooks/customHooks/useGetFleetInstantPayoutType';

export default function GeneralInfo() {
  const {
    data,
    fleetId,
    loadingDetail,
    onCloseModal,
    fetchDetailFleet,
    onCreateOrEditFleetSuccess
  } = useFleetContext();
  const [loading, setLoading] = useState(false);
  const [disabledForm, setDisabledForm] = useState(true);
  const [assigningStripeAccId, setAssigningStripeAccId] = useState(false);
  const viewDetailMode = Boolean(fleetId);
  const viewMode = viewDetailMode && disabledForm;
  const editMode = viewDetailMode && !disabledForm; // enable update item
  const [isAssignExistUser, setIsAssignExistPrincipal] = useState(false);
  const { userData, viewAsRole } = useAuth();
  const byPassCheckAbn = [ROLES.COMPANY, ROLES.FLEET].includes(viewAsRole);

  const formMethods = useForm({
    resolver: yupResolver(genFleetFormSchema(isAssignExistUser)),
    values: data,
    mode: 'all'
  });

  const {
    register,
    watch,
    control,
    reset,
    setError,
    trigger,
    setValue,
    clearErrors,
    formState,
    handleSubmit
  } = formMethods;

  const { errors } = formState;
  const countryCode = watch('address.country_id');
  const groupId = watch('group_id');
  const companyId = watch('company_id');
  const principalCountryCode = watch('user_principal.address.country_id');
  const emailInput = watch('user_principal.email');
  const existEmailInput = watch('existing_principal');
  const abnPrincipalInput = watch('user_principal.tax_number');
  const abnInput = watch('tax_number');
  const { fetchCountries, listCountry, listState } = useGetCountry({ countryId: countryCode });
  const { listState: listPrincipalState } = useGetCountry({ countryId: principalCountryCode });

  const { pendingCheckAbn } = useCheckValidABN({
    abn: abnInput,
    countryId: countryCode,
    countries: listCountry,
    name: 'tax_number',
    validateSpecificField: true,
    clearErrors,
    setError,
    formState,
    bypass: byPassCheckAbn
  });

  const { pendingCheckAbn: pendingCheckPrincipalAbn } = useCheckValidABN({
    abn: abnPrincipalInput,
    countryId: principalCountryCode,
    countries: listCountry,
    name: 'user_principal.tax_number',
    validateSpecificField: true,
    clearErrors,
    setError,
    formState,
    bypass: byPassCheckAbn
  });

  const { errMsg, isValidEmail, pendingCheckingEmail, resetState } = useCheckDuplicateEmail({
    email: emailInput,
    bypass: viewMode || (editMode && emailInput == data.user_principal?.email) || !emailInput
  });
  const { listGroup, fetchListGroup } = useFetchGroup();
  const { companiesByGroup } = useFetchCompany({ groupId });
  const { listFleetInstantPayoutTypes } = useGetFleetInstantPayoutType();

  const onChangePrincipalCountry = e => {
    const principalCountryId = Number(e.target.value) || undefined;
    setValue('user_principal.address.country_id', principalCountryId);
    setValue('user_principal.address.state_id', undefined);
  };

  const onChangeTypePrincipal = e => {
    setIsAssignExistPrincipal(e.target.checked);
    if (!e.target.checked) {
      setValue('existing_principal', undefined);
    }
  };

  const fetchAvailablePrincipal = value => {
    if (!companyId) return;
    return companyServices.getAvailablePrincipal(companyId, {
      user_name: value
    });
  };

  const onReset = () => {
    reset(data);
    setDisabledForm(true);
    setIsAssignExistPrincipal(false);
    setAssigningStripeAccId(false);
  };

  const onChangeGroup = e => {
    const groupId = Number(e.target.value);
    setValue('group_id', Number(groupId) || undefined);

    // prefill country base on group
    const selectedGroup = listGroup?.items?.find(item => item.id === groupId);
    if (selectedGroup?.country_id) {
      setValue('address.country_id', selectedGroup?.country_id);
      trigger('address.country_id');
      setValue('user_principal.address.country_id', selectedGroup?.country_id);
      trigger('user_principal.address.country_id');
      setValue('user_principal.address.state_id', undefined);
    }
  };

  const onEnableEditForm = () => {
    setDisabledForm(false);
  };

  // Sync error message between email form validation and api email validation
  const onValidatePrincipalEmail = () => {
    if (!emailInput) {
      setError('user_principal.email', {
        type: 'optionality',
        message: YupMessage.REQUIRED
      });
      resetState();
      return;
    }

    const errorMsgEmail = getValueObj(errors, 'user_principal.email'.split('.'));

    if (isValidEmailInput(emailInput) && isValidEmail && errMsg && errorMsgEmail?.type === 'custom')
      clearErrors('user_principal.email');

    // By pass if user revert principal email to current principal email
    if (errMsg && !isValidEmail && !(editMode && emailInput == data.user_principal?.email)) {
      setError('user_principal.email', {
        type: 'custom',
        message: errMsg
      });
    }

    if (!isValidEmailInput(emailInput)) {
      setError('user_principal.email', {
        type: 'custom',
        message: YupMessage.INVALID_EMAIL
      });
    }
  };

  const onSubmit = async dataSubmit => {
    try {
      setLoading(true);
      let { existing_principal, ...databody } = dataSubmit;
      databody.assign_flag = false;

      if (existing_principal?.email) {
        databody = {
          ...databody,
          assign_flag: true,
          user_principal: {
            email: existing_principal.email,
            id: existing_principal.id
          }
        };
      }

      if (fleetId) {
        if (databody?.user_principal?.email !== data.user_principal?.email && !isAssignExistUser) {
          databody.user_principal.id = 0;
        }
        await fleetServices.updateFleet(fleetId, databody);
        onCreateOrEditFleetSuccess();
        toast.success('The fleet was successfully updated.');
        return;
      }
      await fleetServices.create(databody);
      onCreateOrEditFleetSuccess();
      toast.success('The fleet was successfully created.');
    } catch (err) {
      if (err?.error?.type === DUPLICATE_EMAIL) {
        setError('user_principal.email', { type: 'optionality', message: err?.error?.message });
      }
      toast.error(err?.error?.message || 'The action has failed.');
    } finally {
      setLoading(false);
    }
  };

  const onSuccess = () => {
    fetchDetailFleet(data.id);
  };

  const [disabledPaymentPoint, setDisabledPaymentPoint] = useState(false);

  useEffect(() => {
    if (viewDetailMode) return;
    if (!emailInput) return;
    onValidatePrincipalEmail();
  }, [emailInput, isValidEmail, errMsg, viewDetailMode]);

  useEffect(() => {
    if (companyId && companiesByGroup.length) {
      const selectedCompany = companiesByGroup.find(item => item.id === companyId) || {};
      setValue('fleet_comm', selectedCompany?.company_comm);
      const disablePP = selectedCompany.payment_point_2_company !== PAYMENT_POINT[1].value;
      setDisabledPaymentPoint(disablePP);
      if (!viewDetailMode) {
        setValue('payment_point', disablePP ? PAYMENT_POINT_FLEET[0].value : '');
      }
    }
  }, [companyId, companiesByGroup, viewDetailMode]);

  useEffect(() => {
    setValue('existing_principal', null);
  }, [companyId]);

  useEffect(() => {
    fetchCountries();
    fetchListGroup({ pageSize: 1000 });
  }, []);

  const isPending =
    loadingDetail || pendingCheckAbn || pendingCheckPrincipalAbn || pendingCheckingEmail;
  const hasFormError = Object.keys(errors).filter(item => item !== 'existing_principal').length > 0;

  const enableCreate = isAssignExistUser
    ? !existEmailInput
    : isPending || hasFormError || !isValidEmailInput(emailInput);

  const canUpdateInstantPayout =
    viewAsRole === ROLES.ADMIN && Number(data?.company_instant_payout_type_editable) === 1;

  const listPaymentPointFleet =
    editMode && !disabledPaymentPoint
      ? PAYMENT_POINT_FLEET.filter(item => {
          if (item.value === PAYMENT_POINT_FLEET[2].value) return false;
          return true;
        })
      : PAYMENT_POINT_FLEET;

  return (
    <FormProvider {...formMethods}>
      <form id="add-update-fleet" onSubmit={handleSubmit(onSubmit)}>
        <Card className="mb-3">
          <Card.Header className="d-flex justify-center align-center bg-primary-subtle">
            <h5 className="mb-0 fs-0 mr-auto">Details</h5>
          </Card.Header>
          <Card.Body className="border-top fs--1 position-relative">
            {loadingDetail && <LoadingOverlay />}
            <Row>
              <Col xs={12} lg={6}>
                <div className="pb-3 pt-1">
                  <span className="fs--1 fw-bolder">Fleet Information</span>
                </div>
                <WizardInpuHorizontal
                  label="Fleet Name"
                  required
                  name="name"
                  errors={errors}
                  placeholder="Fleet Name"
                  {...commonConfig({
                    register: register('name'),
                    disabled: viewMode,
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
                <WizardInpuHorizontal
                  type="select"
                  watch={watch}
                  required
                  options={getListOptions(listCountry)}
                  defaultOption={{
                    label: data?.address?.country_name,
                    value: data?.address?.country_id
                  }}
                  label="Country"
                  name="address.country_id"
                  errors={errors}
                  placeholder="Add country"
                  {...commonConfig({
                    register: register('address.country_id', { valueAsNumber: true }),
                    disabled: true,
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
                <WizardInpuHorizontal
                  type="select"
                  watch={watch}
                  options={getListOptions(listState)}
                  label="State"
                  name="address.state_id"
                  errors={errors}
                  placeholder="Add state"
                  {...commonConfig({
                    register: register('address.state_id', { valueAsNumber: true }),
                    disabled: viewMode,
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
                <WizardInpuHorizontal
                  label="Address"
                  required
                  name="address.detail_address"
                  placeholder="Address"
                  errors={errors}
                  {...commonConfig({
                    register: register('address.detail_address'),
                    disabled: viewMode,
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
                <WizardInpuHorizontal
                  label="Locality"
                  name="address.city"
                  errors={errors}
                  placeholder="Locality"
                  {...commonConfig({
                    register: register('address.city'),
                    disabled: viewMode,
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
                <WizardInpuHorizontal
                  label="Post Code/ Zipcode"
                  name="address.postcode"
                  required
                  errors={errors}
                  placeholder="Post Code/ Zipcode"
                  {...commonConfig({
                    register: register('address.postcode'),
                    disabled: viewMode,
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
                <WizardInpuHorizontal
                  label="Phone number"
                  name="contact_number"
                  errors={errors}
                  placeholder="Phone number"
                  type="phoneInput"
                  control={control}
                  defaultCountry={userData?.address?.countryCode}
                  {...commonConfig({
                    disabled: viewMode,
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
                <WizardInpuHorizontal
                  label="Email"
                  name="email"
                  errors={errors}
                  placeholder="Email"
                  {...commonConfig({
                    register: register('email'),
                    disabled: viewMode,
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
              </Col>
              <Col xs={12} lg={6}>
                <div className="pb-3 pt-1">
                  <span className="fs--1 fw-bolder">Operations</span>
                </div>
                <WizardInpuHorizontal
                  label="Tax ID"
                  type="number"
                  name="tax_number"
                  errors={errors}
                  placeholder="Tax ID"
                  {...commonConfig({
                    register: register('tax_number'),
                    disabled: viewMode,
                    formGroupProps: {
                      className: 'mb-4'
                    },
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
                <WizardInpuHorizontal
                  label="Group"
                  required
                  name="group_id"
                  type="select"
                  watch={watch}
                  errors={errors}
                  placeholder="Add Group"
                  options={getListOptions(listGroup.items || [])}
                  defaultOption={{
                    label: data?.group_name,
                    value: data?.group_id
                  }}
                  {...commonConfig({
                    formControlProps: {
                      onChange: onChangeGroup
                    },
                    register: register('group_id', { valueAsNumber: true }),
                    disabled: viewDetailMode,
                    formGroupProps: {
                      className: 'mb-1 mt-3'
                    },
                    permissions: {
                      VIEW: [ROLES.FLEET],
                      MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                    }
                  })}
                />
                <WizardInpuHorizontal
                  label="Company"
                  name="company_id"
                  type="select"
                  watch={watch}
                  errors={errors}
                  required
                  placeholder="Add Company"
                  options={getListOptions(companiesByGroup)}
                  {...commonConfig({
                    register: register('company_id', { valueAsNumber: true }),
                    disabled: viewDetailMode,
                    formGroupProps: {
                      className: 'mb-1 mt-3'
                    }
                  })}
                />
                <WizardInpuHorizontal
                  label="Instant Payout"
                  name="payee_instant_payout_type_id"
                  errors={errors}
                  placeholder="Instant Payout"
                  type="select"
                  watch={watch}
                  options={getListOptions(listFleetInstantPayoutTypes, 'id', 'name')}
                  defaultOption={{
                    label: data?.payee_instant_payout_type_name,
                    value: data?.payee_instant_payout_type_id
                  }}
                  {...commonConfig({
                    register: register('payee_instant_payout_type_id', {
                      valueAsNumber: true
                    }),
                    disabled: viewMode || !canUpdateInstantPayout,
                    formGroupProps: {
                      className: 'mb-1 mt-3'
                    }
                  })}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Row className="g-2">
          <Col xs={12} lg={6} className="d-flex">
            <Card className="mb-3 flex-1">
              <Card.Header className="d-flex justify-center align-center bg-primary-subtle">
                <h5 className="mb-0 fs-0 mr-auto">Payout Details</h5>
              </Card.Header>
              <Card.Body className="border-top fs--1 position-relative">
                {loadingDetail && <LoadingOverlay />}
                <div className="w-100">
                  <WizardInpuHorizontal
                    label="Payment Point"
                    required
                    name="payment_point"
                    type="select"
                    watch={watch}
                    options={listPaymentPointFleet}
                    errors={errors}
                    placeholder="Select Payment Point"
                    {...commonConfig({
                      register: register('payment_point'),
                      disabled: viewMode || disabledPaymentPoint,
                      formControlProps: {
                        style: {
                          paddingRight: '3rem'
                        }
                      },
                      permissions: {
                        VIEW: [ROLES.FLEET],
                        MODIFY: [ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP, ROLES.COMPANY]
                      }
                    })}
                  />
                  <WizardInpuHorizontal
                    type="number"
                    label="Fleet Rate"
                    name="fleet_comm"
                    errors={errors}
                    {...commonConfig({
                      register: register('fleet_comm', {
                        valueAsNumber: true
                      }),
                      disabled: true
                    })}
                  />
                  <Row className="mb-1">
                    <Col {...formLayout.label}>
                      <p className="label col-form-label mb-0">Stripe connected</p>
                    </Col>
                    <Col {...formLayout.col} className="d-flex gap-3 align-items-center">
                      <StripeConnectStatus
                        id={data.id}
                        type="fleet"
                        stripeConnected={data.stripe_connect_enabled === 1}
                        canModify={[
                          ROLES.ADMIN,
                          ROLES.CABFARE_INTERNAL,
                          ROLES.GROUP,
                          ROLES.COMPANY
                        ].includes(viewAsRole)}
                        onSuccess={onSuccess}
                        countryCode={data.group_country_code}
                      />
                      {viewAsRole === ROLES.ADMIN && editMode && (
                        <LoadingButton
                          name="Assign Existing"
                          className="btn-info fs--2 justify-content-center d-flex"
                          btnProps={{
                            onClick: () => setAssigningStripeAccId(true),
                            size: 'sm'
                          }}
                        />
                      )}
                    </Col>
                  </Row>
                  {assigningStripeAccId ? (
                    <WizardInpuHorizontal
                      label="Stripe Account ID"
                      name="stripe_account_id"
                      errors={errors}
                      placeholder="Stripe Account ID"
                      {...commonConfig({
                        disabled: false,
                        register: register('stripe_account_id')
                      })}
                    />
                  ) : (
                    <Row className="mb-1">
                      <Col {...formLayout.label}>
                        <span className="label">Stripe Account ID</span>
                      </Col>
                      <Col {...formLayout.col}>{data.stripe_account_id}</Col>
                    </Row>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} lg={6} className="d-flex">
            <PrincipalDetailForm
              data={data}
              loading={loadingDetail}
              viewMode={viewMode}
              pageKey={FLEET}
              isDisabled={!companyId}
              listCountry={listCountry}
              listState={listPrincipalState}
              viewDetailMode={viewDetailMode}
              onBlurEmailInput={onValidatePrincipalEmail}
              isAssignExistUser={isAssignExistUser}
              isTaxNumberRequired={false}
              typePrincipal={FLEET_PRINCIPAL}
              onChangeCountry={onChangePrincipalCountry}
              onChangeTypePrincipal={onChangeTypePrincipal}
              fetchAvailablePrincipal={fetchAvailablePrincipal}
            />
          </Col>
        </Row>
      </form>
      <div className="d-flex ml-auto px-4 py-2 position-absolute w-100 bottom-0 start-0 justify-content-end bg-body-quaternary border-top">
        <FooterActionBtn
          id={fleetId}
          onClose={onCloseModal}
          loading={loading}
          onReset={onReset}
          disabled={enableCreate}
          viewMode={viewMode}
          viewDetailMode={viewDetailMode}
          onEdit={onEnableEditForm}
          formName="add-update-fleet"
        />
      </div>
    </FormProvider>
  );
}
